import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, XCircle, FileText, HardHat } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const NAVY = '#16324f';

const fmtDate = (d) => {
  if (!d) return null;
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return d; }
};

const BrandHeader = () => (
  <div style={{ backgroundColor: NAVY }} className="border-b-4 border-blue-500 px-5 py-5">
    <div className="max-w-lg mx-auto flex items-center gap-4">
      <img src="/365-logo-white.png" alt="365 Industrial Solutions" className="h-14 w-auto flex-shrink-0" data-testid="verify-brand-logo" />
      <div>
        <p className="text-blue-300 text-[11px] font-bold tracking-widest uppercase">Safety Ticket Verification</p>
        <p className="text-white text-base font-bold leading-tight">365 Industrial Solutions</p>
        <p className="text-blue-200/70 text-[11px]">Live record — no login required</p>
      </div>
    </div>
  </div>
);

export default function PublicCertVerify() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showExpired, setShowExpired] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/verify/${token}`);
        if (!res.ok) { setError('invalid'); return; }
        setData(await res.json());
      } catch {
        setError('network');
      }
    })();
  }, [token]);

  if (error) {
    return (
      <div style={{ backgroundColor: NAVY }} className="min-h-screen">
        <BrandHeader />
        <div className="flex items-center justify-center p-6 mt-16">
          <div className="text-center" data-testid="verify-error">
            <XCircle className="h-14 w-14 text-red-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white">
              {error === 'invalid' ? 'Invalid verification code' : 'Could not reach the server'}
            </h1>
            <p className="text-blue-200/70 text-sm mt-2">
              {error === 'invalid'
                ? 'This QR code is not recognized. Contact 365 Industrial Solutions to confirm.'
                : 'Check your connection and try again.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ backgroundColor: NAVY }} className="min-h-screen">
        <BrandHeader />
        <div className="flex flex-col items-center justify-center mt-24 gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-300"></div>
          <p className="text-blue-200/70 text-sm">Loading live ticket record…</p>
        </div>
      </div>
    );
  }

  const valid = data.tickets.filter(t => t.valid);
  const expired = data.tickets.filter(t => !t.valid);

  return (
    <div style={{ backgroundColor: NAVY }} className="min-h-screen pb-10">
      <BrandHeader />

      <div className="max-w-lg mx-auto px-4 mt-5 space-y-4">
        {/* Employee card */}
        <div className="bg-white rounded-xl p-5 shadow-lg" data-testid="verify-employee-card">
          <div className="flex items-center gap-3">
            <div style={{ backgroundColor: '#e8eef5' }} className="h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0">
              <HardHat className="h-6 w-6" style={{ color: NAVY }} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900" data-testid="verify-employee-name">{data.name}</h1>
              <p className="text-sm">
                <span className="text-emerald-600 font-semibold">{data.valid_count} valid ticket{data.valid_count === 1 ? '' : 's'}</span>
                {data.expired_count > 0 && <span className="text-slate-400"> · {data.expired_count} expired</span>}
              </p>
            </div>
          </div>
          {data.pdf_available && (
            <a
              href={`${BACKEND_URL}/api/verify/${token}/pdf`}
              target="_blank"
              rel="noreferrer"
              style={{ backgroundColor: NAVY }}
              className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 text-white rounded-lg font-semibold text-sm hover:opacity-90"
              data-testid="verify-pdf-link"
            >
              <FileText className="h-4 w-4" /> View Ticket PDF (card copies)
            </a>
          )}
        </div>

        {/* Valid tickets */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-100">
            <h2 className="text-sm font-bold text-emerald-800 uppercase tracking-wide">Current Tickets & Certifications</h2>
          </div>
          {valid.length === 0 ? (
            <p className="p-4 text-sm text-slate-500" data-testid="verify-no-valid">No current tickets on record.</p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="verify-valid-list">
              {valid.map((t, i) => (
                <li key={i} className="px-4 py-3 flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 text-sm">{t.name}</p>
                    <p className="text-xs text-slate-500">
                      {t.expiry_date ? `Expires ${fmtDate(t.expiry_date)}` : 'No expiry'}
                      {t.organization ? ` · ${t.organization}` : ''}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Expired */}
        {expired.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <button
              onClick={() => setShowExpired(!showExpired)}
              className="w-full px-4 py-3 text-left text-sm font-semibold text-slate-500 flex items-center justify-between"
              data-testid="verify-expired-toggle"
            >
              <span>Expired / superseded ({expired.length})</span>
              <span>{showExpired ? '▲' : '▼'}</span>
            </button>
            {showExpired && (
              <ul className="divide-y divide-slate-100 border-t border-slate-100" data-testid="verify-expired-list">
                {expired.map((t, i) => (
                  <li key={i} className="px-4 py-3 flex items-start gap-3 opacity-70">
                    <XCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-700 text-sm">{t.name}</p>
                      <p className="text-xs text-red-500">Expired {fmtDate(t.expiry_date)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="flex items-center justify-center gap-2 pt-2" data-testid="verify-footer">
          <img src="/365-logo-white.png" alt="" className="h-6 w-auto opacity-70" />
          <p className="text-center text-xs text-blue-200/70">
            365 Industrial Solutions · Verified live from 365Ops records · {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </div>
    </div>
  );
}
